import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { LOGO } from "../lib/content";
import { toast } from "sonner";
import { ExternalLink, Sparkles } from "lucide-react";

const FALLBACK_CREATORS = [
  { _id: "c1", name: "Imani Royale", tiktok_handle: "@imani.royale", tiktok_url: "https://www.tiktok.com/@imani.royale", niche: "Luxury Lifestyle", follower_count: "412K", photo_url: "https://images.unsplash.com/photo-1591348278999-ee1d0c06ed7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpbmZsdWVuY2VyJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc4MDYwMjUxMHww&ixlib=rb-4.1.0&q=85", bio: "Editorial lifestyle and travel commentary." },
  { _id: "c2", name: "Marcus Vaughn", tiktok_handle: "@marcus.vaughn", tiktok_url: "https://www.tiktok.com/@marcus.vaughn", niche: "Executive Style", follower_count: "228K", photo_url: "https://images.unsplash.com/photo-1612242879330-cd06b2696e56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcGhvdG9zaG9vdCUyMHN0dWRpb3xlbnwwfHx8fDE3ODA2MDI1MTB8MA&ixlib=rb-4.1.0&q=85", bio: "Boardroom-ready style breakdowns." },
  { _id: "c3", name: "Layla Reyes", tiktok_handle: "@layla.reyes", tiktok_url: "https://www.tiktok.com/@layla.reyes", niche: "Production & Behind-the-Scenes", follower_count: "586K", photo_url: "https://images.unsplash.com/photo-1536293283170-b4604bbe272f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcGhvdG9zaG9vdCUyMHN0dWRpb3xlbnwwfHx8fDE3ODA2MDI1MTB8MA&ixlib=rb-4.1.0&q=85", bio: "Inside the studio of major productions." },
];

const NICHES = ["Luxury Lifestyle", "Executive / Career", "Faith & Pulpit", "Production", "Beauty & Image", "Politics & Civic", "Other"];

export default function CreatorsPage() {
  const [creators, setCreators] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", tiktok_handle: "", follower_count: "", niche: NICHES[0], other_platforms: "", why_join: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/creators").then((r) => setCreators(r.data.length ? r.data : FALLBACK_CREATORS)).catch(() => setCreators(FALLBACK_CREATORS));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/creators/apply", form);
      toast.success("Application received. We'll be in touch.");
      setForm({ full_name: "", email: "", phone: "", tiktok_handle: "", follower_count: "", niche: NICHES[0], other_platforms: "", why_join: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--crest-black)] text-[var(--crest-cream)] on-dark" data-testid="creators-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-16">
        <div className="flex items-center gap-3 eyebrow" style={{ color: "var(--crest-gold)" }}>
          <Sparkles size={14} /> TikTok Creator Network
        </div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight mt-6 leading-[1.02] max-w-4xl">
          A signed network of creators built for prestige partnerships.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[var(--crest-cream)]/65">
          Upper Crest Image curates a private network of TikTok creators ready to partner with luxury brands, faith leaders,
          campaigns, and lifestyle marques. Apply below to join.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>— Featured Creators</div>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {creators.map((c, i) => (
            <a key={c._id || i} href={c.tiktok_url} target="_blank" rel="noreferrer" className="group block" data-testid={`creator-${i}`}>
              <div className="aspect-square overflow-hidden">
                <img src={c.photo_url} alt={c.name} className="luxe-img w-full h-full object-cover" />
              </div>
              <div className="pt-5">
                <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>{c.niche}</div>
                <div className="font-serif text-2xl mt-2 group-hover:text-[var(--crest-gold)] transition-colors flex items-center gap-2">
                  {c.name} <ExternalLink size={14} />
                </div>
                <div className="text-sm text-[var(--crest-cream)]/60 mt-1">{c.tiktok_handle}{c.follower_count ? ` · ${c.follower_count} followers` : ""}</div>
                {c.bio && <p className="text-sm text-[var(--crest-cream)]/60 mt-3">{c.bio}</p>}
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-2 gap-12">
        <div>
          <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>— Benefits</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Why join the network</h2>
          <ul className="mt-8 space-y-5 text-[var(--crest-cream)]/75">
            <li className="flex gap-3"><span style={{ color: "var(--crest-gold)" }}>◆</span> Brand-paid partnerships with vetted luxury & lifestyle marques.</li>
            <li className="flex gap-3"><span style={{ color: "var(--crest-gold)" }}>◆</span> Creative direction and shoot support from the studio.</li>
            <li className="flex gap-3"><span style={{ color: "var(--crest-gold)" }}>◆</span> Production-led content briefs that elevate (not compromise) your voice.</li>
            <li className="flex gap-3"><span style={{ color: "var(--crest-gold)" }}>◆</span> Roster placement for invitation-only campaigns and events.</li>
          </ul>
          <img src={LOGO.gold} alt="" className="h-24 w-24 mt-12 opacity-90" />
        </div>

        <form onSubmit={submit} className="space-y-1" data-testid="creator-apply-form">
          <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>— Apply</div>
          <h3 className="font-serif text-4xl tracking-tight mt-3 mb-6">Tell us about you</h3>
          <input className="crest-input" placeholder="Full Name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="creator-name" />
          <input type="email" className="crest-input" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="creator-email" />
          <input className="crest-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="creator-phone" />
          <input className="crest-input" placeholder="TikTok handle (e.g., @yourname)" required value={form.tiktok_handle} onChange={(e) => setForm({ ...form, tiktok_handle: e.target.value })} data-testid="creator-handle" />
          <input className="crest-input" placeholder="Follower count" required value={form.follower_count} onChange={(e) => setForm({ ...form, follower_count: e.target.value })} data-testid="creator-followers" />
          <select className="crest-input" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} data-testid="creator-niche">
            {NICHES.map((n) => <option key={n}>{n}</option>)}
          </select>
          <input className="crest-input" placeholder="Other platforms (optional)" value={form.other_platforms} onChange={(e) => setForm({ ...form, other_platforms: e.target.value })} data-testid="creator-other" />
          <textarea className="crest-input" placeholder="Why do you want to join the Upper Crest network?" required value={form.why_join} onChange={(e) => setForm({ ...form, why_join: e.target.value })} data-testid="creator-why" />
          <button className="btn-primary mt-6 w-full" style={{ background: "var(--crest-gold)", color: "var(--crest-black)", borderColor: "var(--crest-gold)" }} disabled={submitting} data-testid="creator-submit">
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </section>
    </div>
  );
}
